import * as React from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer } from "../atoms/spacers"
import H3 from "../atoms/typography/H3"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import MemberProfileOverview from "./MemberProfileOverview"
import { useRemoveTerminalsFromGroup } from "../../hooks/useRemoveTerminalsFromGroup"
import { MediaQuery } from "../../utils/design-helpers"
import { MinusIcon } from "../atoms/icons/MinusIcon"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  terminal: {
    src: string
    name: string
    serialNumber: string
    id: string | number
  }
  isEditing?: boolean
  groupId: number | string
  orgId: number | string
}

const AddMemberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ContentContainer = styled.div<{ isOpen?: boolean }>`
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: ${(props) => props.theme.spacings.medium};
  background-color: ${(props) => (props.isOpen ? props.theme.colors.greyLight : "transparent")};

  cursor: pointer;

  ${MediaQuery.MD} {
    :hover {
      background-color: ${(props) => props.theme.colors.greyLight};
    }

    :hover .hiddensection {
      display: block;
    }
  }
`

const IconContainer = styled.div`
  cursor: pointer;
  display: none;

  :hover {
    filter: brightness(60%);
  }

  ${MediaQuery.XS} {
    display: block;
  }
`

const ActionContainer = styled.div`
  padding: ${(props) => props.theme.spacings.large};
`

const TerminalCardWithActions: React.FunctionComponent<Props> = ({ terminal, isEditing, groupId, orgId }) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const onCompletedRemoveFromGroup = () => {
    console.log("Removed terminal from group")
  }
  const onErrorRemoveFromGroup = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error removing terminal from group")
  }

  const { removeTerminalsFromGroup } = useRemoveTerminalsFromGroup({
    onCompleted: onCompletedRemoveFromGroup,
    onError: onErrorRemoveFromGroup,
    terminalId: "n/a",
  })

  const onClickRemoveFromGroup = async () => {
    try {
      await removeTerminalsFromGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          terminalIds: [{ id: terminal.id }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing terminal from group")
    }
  }

  return (
    <div>
      {!isEditing && <MemberProfileOverview imgSrc={terminal.src} name={terminal.name} role={terminal.serialNumber} />}
      {isEditing && (
        <div>
          <ContentContainer isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
            <AddMemberContainer>
              <MemberProfileOverview imgSrc={terminal.src} name={terminal.name} role={""} />
              <IconContainer onClick={() => setIsOpen(!isOpen)} className="hiddensection">
                {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconContainer>
            </AddMemberContainer>
            <SlideOutHeight isVisible={isOpen}>
              <ActionContainer>
                <H3>Serial number: {terminal.serialNumber}</H3>
                <HorizontalSpacer spacing={1} />
                <Link to="/dashboard/terminals">
                  <PrimaryButton title="View in terminals" />
                </Link>
                <HorizontalSpacer spacing={1} />
                <PrimaryButton title="Remove from group" icon={<MinusIcon />} onClick={onClickRemoveFromGroup} />
              </ActionContainer>
            </SlideOutHeight>
          </ContentContainer>
        </div>
      )}
    </div>
  )
}

export default TerminalCardWithActions
